import {
  useCallback,
  useEffect,
  useRef,
  // Aliased so the DOM's own `KeyboardEvent` stays reachable: the document-level
  // backstop below is a native listener, not a React one.
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { FOCUSABLE_SELECTOR } from '../a11y';

/**
 * useLearningModal — the keyboard + focus contract for the Learning page's three
 * dialogs (training, self-assessment, About/FAQ), which are drawn by the upload
 * flow's `OverlayFrame` + `ModalShell`.
 *
 * WHY NOT `a11y.ts`'s `useFocusTrap`. That hook does three things: it focuses the
 * first focusable on mount, traps Tab, and restores focus to
 * `document.activeElement` as captured in its own effect. Composed with
 * `OverlayFrame` the first and third fight it: OverlayFrame focuses the dialog
 * container in a ref callback, which runs in the COMMIT phase — before any
 * effect — so `useFocusTrap` would capture the dialog itself as "previously
 * focused" and, on close, restore focus to a node that no longer exists. Focus
 * would land at the top of the document.
 *
 * So this hook does the three jobs OverlayFrame does not:
 *   - Tab cycling + Escape, as a React `onKeyDown` handed to OverlayFrame. This
 *     is `useUploadOverlay`'s handler, so the three Learning dialogs and the
 *     upload wizard trap Tab identically (PC-NFR01 → WCAG 2.1.2, 2.4.3).
 *   - Focus RESTORE, from a ref to the trigger passed in by the caller — the
 *     house pattern the Profile panels and ShareOverlay already use
 *     (`returnFocusRef`). `useUploadOverlay` has no restore at all; a dialog
 *     opened from a section heading must put focus back on that heading's button,
 *     not at the top of the page.
 *   - A capture-phase `keydown` listener on `document`, live only while focus has
 *     escaped the dialog subtree. The React `onKeyDown` is bound to the dialog, so it
 *     stops firing the moment focus lands on <body> — which is what happens when the
 *     node holding focus unmounts (a step change) or gains `disabled` (a saving
 *     button; `Button` renders the real attribute). Without the backstop, Escape
 *     stops closing and Tab walks the page behind the scrim.
 *
 * The first-focus job is deliberately absent: OverlayFrame already focuses the
 * dialog container (`tabIndex={-1}`), which puts a screen reader at the dialog's
 * accessible name and its first Tab inside the trap.
 */
export interface LearningModalHandles {
  /** Hand to `OverlayFrame`'s `dialogRef` — it focuses this node on mount. */
  dialogRef: MutableRefObject<HTMLDivElement | null>;
  /** Hand to `OverlayFrame`'s `onKeyDown`. */
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

export function useLearningModal(
  onClose: () => void,
  /** The control that opened the dialog; focus returns here when it unmounts. */
  returnFocusRef?: RefObject<HTMLButtonElement>,
): LearningModalHandles {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Read the trigger on MOUNT, not in the cleanup closure: by the time cleanup runs
  // the ref may already have been repointed (the page keeps one ref per section
  // heading, and React clears refs for unmounting nodes before parent cleanups).
  useEffect(() => {
    const trigger = returnFocusRef?.current ?? null;
    return () => {
      trigger?.focus();
    };
  }, [returnFocusRef]);

  // The backstop for focus that has left the dialog entirely — see the third bullet
  // above. Deliberately capture-phase so it runs before the page's own document-level
  // handlers (the global search suggestions, the profile menu) get the same Escape.
  useEffect(() => {
    function onDocumentKeyDown(event: KeyboardEvent) {
      const root = dialogRef.current;
      // `contains` is true when the container ITSELF holds focus, which the React
      // handler below already covers; this fires only once focus has escaped.
      if (!root || root.contains(document.activeElement)) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const visible = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );
      event.preventDefault();
      // Pull focus back to the edge the user was heading for; the container is the
      // fallback when the dialog currently renders no visible control at all.
      const target = event.shiftKey ? visible[visible.length - 1] : visible[0];
      (target ?? root).focus();
    }
    document.addEventListener('keydown', onDocumentKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown, true);
    };
  }, [onClose]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        // Stop here: the page beneath has its own Escape handlers (the global search
        // suggestions, the profile menu) and must not also react to this one.
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      // `offsetParent === null` drops a control in a hidden branch (a step that is not the
      // current one). The container is NOT in this list, despite what `useUploadOverlay`'s
      // copy of this line implies: `querySelectorAll` returns descendants only, and
      // FOCUSABLE_SELECTOR ends in `[tabindex]:not([tabindex="-1"])` (a11y.ts:29) while
      // OverlayFrame puts exactly `tabIndex={-1}` on it (OverlayFrame.tsx:130). The
      // shift-from-container guard below is the container's only handling.
      const visible = focusable.filter((el) => el.offsetParent !== null);
      if (visible.length === 0) {
        // Nothing visible to cycle. Hold focus on the container rather than return and let
        // native Tab carry the user out onto the page behind the scrim (LH-NFR02).
        // Unreachable today — all three dialogs render a footer button — but the trap should
        // not quietly depend on that staying true.
        event.preventDefault();
        root.focus();
        return;
      }

      const first = visible[0]!;
      const last = visible[visible.length - 1]!;
      // OverlayFrame focuses the dialog CONTAINER on open (`tabIndex={-1}`), which is
      // neither `first` nor `last` — so without this, the very first Shift+Tab matches
      // neither guard below and native backwards navigation carries the user out of the
      // dialog and onto the page behind the scrim (LH-NFR02).
      if (event.shiftKey && document.activeElement === root) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    },
    [onClose],
  );

  return { dialogRef, onKeyDown };
}
